import { Button, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ConfirmStep from "../../components/VMRS/ExploreTasking/ConfirmStep";
import Step1Comp from "../../components/VMRS/ExploreTasking/Step1";
import DynamicStepRenderer from "../../components/VMRS/ExploreTasking/components/DynamicStepRenderer";
import { useWorkflow } from "../../components/VMRS/ExploreTasking/hook/useWorkflow";
import { UniversalMap } from "../../components/VMRS/MapAdapter/UniversalMap";
import
{
   removeAOIFeature,
   setDrawingAOIMode,
   setFeatureVisibility,
   showAllAOI,
} from "../../components/VMRS/MapAdapter/store/slices/aoiSlice";
import ContentLayoutManagement from "../../layouts/ContentLayoutManagement";
import HeaderContent from "../../layouts/HeaderContent";
import { RootState } from "../../store/store";

import { clearAOIFeatures } from "../../components/VMRS/MapAdapter/store/slices/aoiSlice";
import { clearAllLayers } from "../../components/VMRS/MapAdapter/store/slices/layerSlice";

import CommonComponents from "../../components/Commons";
import { clearFeatures as clearDrawFeatures, setDrawingMode } from "../../components/VMRS/MapAdapter/store/slices/drawSlice";
import { clearSegments as clearMeasureSegments, setMeasureMode } from "../../components/VMRS/MapAdapter/store/slices/measureSlice";
import { getAOIAreaLimits, getSupportedAOITypes, getUnsupportedAOIFeatures } from "../../components/VMRS/ExploreTasking/ultis";
import useToggle from "../../hooks/useToggle";
import { getSatellitesAsync } from "../../store/reducers/orderSlice";
import { getTaskingProviders } from "../../services/mapServices";

// pages/ExploreTaskingPage.tsx
const ExploreTaskingPage = () =>
{
   const { t } = useTranslation();
   // Hãng được phép đặt chụp mới (BE lọc theo nhóm user). Có thể rỗng.
   const [allProviders, setAllProviders] = useState<any[]>([]);
   const [providersLoading, setProvidersLoading] = useState(true);

   const [selectedProvider, setSelectedProvider] = useState(null);
   const [selectedSatellite, setSelectedSatellite] = useState<any>(null);
   const [mode, setMode] = useState<"public" | "workflow" | "confirm">("public"); // mode pulic is step provider, workflow is step by step form schema driven
   const [loading, setLoading] = useState(false);

   const [satellites, setSatellites] = useState<any[]>([]);

   const allAOI = useSelector((state: RootState) => state.aoi.features);

   let activeFeatureId = useSelector(
      (state: RootState) => state.aoi.activeFeatureId,
   );

   const dispatch = useDispatch();

   const {
      open: openConfirm,
      toggle: toggleConfirm,
      shouldRender: shouldConfirm,
   } = useToggle();

   const {
      currentStep,
      currentStepIndex,
      totalSteps,
      formData,
      setFormData,
      next,
      back,
      submit,
      fieldMeta,
      stepFieldMap,
      workflow,
      setCurrentStepIndex,
      conFirmSubmit,
   } = useWorkflow(
      selectedProvider,
      setLoading,
      toggleConfirm,
      selectedSatellite,
   );

   const handleNext = () =>
   {
      if (currentStepIndex + 1 === totalSteps - 1)
      {
         setMode("confirm");
      } else
      {
         next();
      }
   };

   const handleBack = () =>
   {
      if (mode === "confirm")
      {
         setMode("workflow");
      } else if (currentStepIndex === 0)
      {
         setMode("public");
      } else
      {
         back();
      }
   };

   useEffect(() =>
   {
      let cancelled = false;
      getTaskingProviders()
         .then((res: any) =>
         {
            if (cancelled) return;
            const list = (res?.data?.data ?? []).filter(
               (item: any) => item?.is_active && item?.is_tasking,
            );
            setAllProviders(list);
            // Hãng đang chọn không còn được phép => bỏ chọn
            setSelectedProvider((prev: any) =>
               prev && !list.some((p: any) => p?.code === prev) ? null : prev,
            );
         })
         .catch((e: any) =>
         {
            console.error(e);
            if (!cancelled) setAllProviders([]);
         })
         .finally(() =>
         {
            if (!cancelled) setProvidersLoading(false);
         });
      return () =>
      {
         cancelled = true;
      };
   }, []);

   useEffect(() =>
   {
      setSatellites([]);
      if (!selectedProvider)
      {
         return;
      }

      handleGetSatellites();
   }, [selectedProvider]);

   // Which AOI shapes the selected provider accepts, straight from its flow
   // schema (`ui.options.geometry.supportedTypes`) — no provider-code special
   // casing. null means the answer isn't known yet: no provider picked, or the
   // newly picked one's flow is still being fetched (useWorkflow blanks the
   // workflow the moment the provider changes).
   // Area bounds travel with the same geometry field as the shape list, so they
   // change with the provider exactly as the shapes do.
   const aoiAreaLimits = useMemo(
      () => getAOIAreaLimits(workflow) ?? undefined,
      [workflow],
   );

   const declaredAOITypes = useMemo(
      () => getSupportedAOITypes(workflow),
      [workflow],
   );

   // A flow that declares nothing means polygons only — that's what the toolbar
   // offers to draw with.
   const supportedAOITypes = useMemo(
      () => declaredAOITypes ?? ["Polygon"],
      [declaredAOITypes],
   );

   // Only the *set* of shapes matters below. The workflow object is replaced
   // again every time a step schema loads, and re-running on that would cancel a
   // draw in progress for nothing.
   const declaredAOIKey = declaredAOITypes?.join("|") ?? "";

   useEffect(() =>
   {
      // The tool being drawn with may have just left the toolbar.
      dispatch(setDrawingAOIMode("NONE"));

      // Drop only what the new provider genuinely can't take; a shape it still
      // supports stays drawn. Nothing is dropped while the shapes are unknown
      // (provider just changed, flow in-flight) — pruning against the Polygon
      // fallback there is what used to delete a Point on the way to a provider
      // that does accept points.
      getUnsupportedAOIFeatures(allAOI, declaredAOITypes).forEach((f) =>
         dispatch(removeAOIFeature(f.id)),
      );
   }, [declaredAOIKey]);

   const handleGetSatellites = async () =>
   {
      try
      {
         setLoading(true);
         // @ts-ignore
         const res = await dispatch(
            // @ts-ignore
            getSatellitesAsync({
               providerCode: selectedProvider,
               skip: 0,
               limit: 100,
            }),
         ).unwrap();

         setSatellites(res?.data?.data);
         setSelectedSatellite(
            res?.data?.data?.length ? [res.data.data[0].name] : [], // note: backend yêu cầu truyền tên satellite thay vì id
         );
      } catch (error)
      {
      } finally
      {
         setLoading(false);
      }
   };

   useEffect(() =>
   {
      if (!allAOI?.length) return;

      if (mode !== "public")
      {
         allAOI.forEach((feat) =>
         {
            const isActive = feat.id === activeFeatureId;

            if (feat.visible !== isActive)
            {
               dispatch(
                  setFeatureVisibility({
                     id: feat.id,
                     visible: isActive,
                  }),
               );
            }
         });

         dispatch(setDrawingAOIMode("NONE"));
      } else
      {
         dispatch(showAllAOI());
      }
   }, [mode, activeFeatureId]);

   const handleResetMap = () =>
   {
      dispatch(clearAOIFeatures());
      dispatch(clearDrawFeatures());


      dispatch(clearMeasureSegments());
      dispatch(clearAllLayers());

      dispatch(setDrawingAOIMode("NONE"));
      dispatch(setDrawingMode("NONE"));
      dispatch(setMeasureMode("NONE"));
   };

   useEffect(() =>
   {
      return () =>
      {
         handleResetMap();
      };
   }, []);

   return (
      <ContentLayoutManagement
         header={<HeaderContent breadcrumb={[{ title: t("Explore Tasking") }]} />}
      >
         <div className="h-full flex bg-[#0f172a] text-white">
            <div className="w-5/12 min-w-[380px] shrink-0 flex flex-col border-r border-[#26324a]">
               <Spin
                  spinning={loading || providersLoading}
                  size="large"
                  className="absolute z-10"
               >
                  {mode === "public" && (
                     <Step1Comp
                        allProviders={allProviders}
                        selectedProvider={selectedProvider}
                        selectedSatellite={selectedSatellite}
                        setSelectedProvider={(val?: any) => setSelectedProvider(val)}
                        setSelectedSatellite={(val?: any) => setSelectedSatellite(val)}
                        onOrder={() => setMode("workflow")}
                        totalSteps={totalSteps}
                        loading={loading}
                        providersLoading={providersLoading}
                        setLoading={setLoading}
                        workflow={workflow}
                        satellites={satellites}
                     />
                  )}

                  {mode === "workflow" && (
                     <>
                        <DynamicStepRenderer
                           step={currentStep}
                           formData={formData}
                           setFormData={setFormData}
                           currentStepIndex={currentStepIndex}
                           totalSteps={totalSteps}
                           next={handleNext}
                           back={handleBack}
                           submit={submit}
                           onBackToSelect={() => setMode("public")}
                           fieldMeta={fieldMeta}
                           stepFieldMap={stepFieldMap}
                           workflow={workflow}
                           setCurrentStepIndex={setCurrentStepIndex}
                           satellites={satellites}
                           selectedSatellite={selectedSatellite}
                           allProviders={allProviders}
                           selectedProvider={selectedProvider}
                        />
                     </>
                  )}
                  {mode === "confirm" && (
                     <ConfirmStep
                        formData={formData}
                        fieldMeta={fieldMeta}
                        stepFieldMap={stepFieldMap}
                        workflow={workflow}
                        onBack={handleBack}
                        onSubmit={submit}
                        currentStepIndex={currentStepIndex}
                        totalSteps={totalSteps}
                     />
                  )}

                  {shouldConfirm && (
                     <CommonComponents.ModalUI
                        title={t("Confirm")}
                        open={openConfirm}
                        toggle={toggleConfirm}
                        handleConfirm={() => conFirmSubmit()}
                        confirmText={t("Save")}
                        extraFooter={
                           <Button
                              className="h-8 rounded btn-primary px-3"
                              onClick={() => conFirmSubmit(true)}
                           >
                              {t("Save and send request")}
                           </Button>
                        }
                     >
                        <p>
                           {t("Are you sure you want to create this request order?")}
                        </p>
                     </CommonComponents.ModalUI>
                  )}
               </Spin>
            </div>

            <div className="w-7/12 flex-1 min-w-0 relative">
               <UniversalMap
                  mapType="maplibre"
                  showEngine={false}
                  showDraw={false}
                  showDrawAOI={mode === "public"}
                  showMeasure={false}
                  view={{
                     center: [108.23653393802581, 16.1039225743402],
                     zoom: 5,
                  }}
                  showBaseMap={true}
                  showMapControl={true}
                  showSearchOSM={false}
                  allowMoveActive={mode === "public"}
                  supportedAOITypes={supportedAOITypes}
                  aoiAreaLimits={aoiAreaLimits}
               />
            </div>
         </div>
      </ContentLayoutManagement>
   );
};

export default ExploreTaskingPage;
